import { useApolloClient, useMutation } from '@apollo/client'
import { ReactElement, useRef, useState } from 'react'
import { defineMessages, FormattedMessage, useIntl } from 'react-intl'

import { Column } from 'components/v2/Column'
import { Row } from 'components/v2/Row'
import { Button } from 'components/v2/Button'
import { ErrorMessage } from 'components/v2/ErrorMessage'
import Heading from 'components/v2/Heading'
import Modal from 'components/v2/Modal'
import { CenteredText } from 'components/Text'
import { GTChangeTripConfirmationModalitiesInput, GTTripV2, GTTripV2ConfirmationModality } from 'gql/types.generated'
import { CHANGE_TRIP_CONFIRMATION_MODALITIES } from 'features/Trips/mutations-legacy'
import { getShortId } from 'utils/string'
import { isEmpty } from 'lodash'
import { IChangedEntityWithAllPromise } from 'types/index'
import { ImpactedSalesChannelsNotification } from 'features/Trips/components/ImpactedSalesChannelsNotification'
import {
  getImpactedSalesChannels,
  getUpdatedConfirmationModalities
} from 'features/Trips/components/AddReservedConfirmationModalityModal/utils'

const messages = defineMessages({
  changeModalityError: {
    id: 'trips.changeModality.rrror',
    defaultMessage: 'Error changing modality for trip {tripId}'
  }
})
interface IAddReservedConfirmationModalityModalProps {
  trips: GTTripV2[]
  closeModal: () => void
  onSuccess?: () => void
}

export const AddReservedConfirmationModalityModal = ({
  trips,
  closeModal,
  onSuccess
}: IAddReservedConfirmationModalityModalProps): ReactElement => {
  const tripsRef = useRef(trips)

  const intl = useIntl()
  const client = useApolloClient()
  const [errorsMessages, setErrorsMessages] = useState<string[]>([])
  const [loading, setLoading] = useState(false)

  const [changeConfirmationModalities] = useMutation(CHANGE_TRIP_CONFIRMATION_MODALITIES)

  const addReservedModality = async (trip: GTTripV2): Promise<void> => {
    let newConfirmationModalities: GTTripV2ConfirmationModality[] = []

    try {
      newConfirmationModalities = getUpdatedConfirmationModalities(trip.confirmationModalities)
    } catch (ex) {
      throw new Error('Paid modality not found for trip', trip.id)
    }

    const input: GTChangeTripConfirmationModalitiesInput = {
      id: trip.id,
      confirmationModalities: newConfirmationModalities.map((modality) => {
        return {
          name: modality.name,
          salesChannelIDs: modality.salesChannels?.map((channel) => channel.id),
          exceptions: {
            placeIDs: modality.exceptions?.places.map((place) => place.id)
          }
        }
      })
    }
    await changeConfirmationModalities({
      variables: {
        input
      }
    })
    client.cache.modify({
      id: client.cache.identify(trip),
      fields: {
        confirmationModalities: () => {
          return newConfirmationModalities
        }
      }
    })
  }

  const handleChangeModality = async (): Promise<void> => {
    setLoading(true)
    setErrorsMessages([])

    try {
      const changeTripsModality = trips.map(addReservedModality)
      const changeTripsModalityResults = await Promise.allSettled(changeTripsModality)
      const tripsWithResult = trips.map((trip, index) => ({
        ...trip,
        ...changeTripsModalityResults[index]
      }))
      const notChangedTrips: IChangedEntityWithAllPromise[] = tripsWithResult.filter(
        ({ status }) => status === 'rejected'
      )
      setLoading(false)
      if (!isEmpty(notChangedTrips)) {
        setErrorsMessages(
          notChangedTrips.map(({ id }): string => {
            return intl.formatMessage(messages.changeModalityError, {
              tripId: getShortId(id)
            })
          })
        )
      } else {
        closeModal()
        onSuccess?.()
      }
    } catch (error) {
      setLoading(false)
      // Do nothing
    }
  }

  const impactedSalesChannels = getImpactedSalesChannels(tripsRef.current)

  return (
    <Modal.Container>
      <Modal.Header>
        <Heading.H5>
          <FormattedMessage
            id="trips.changeConfirmationModalities.addReservedModality.title"
            defaultMessage="Add Reserved Modality"
          />
        </Heading.H5>
      </Modal.Header>
      <Modal.Content>
        <Row>
          <Column>
            <CenteredText>
              <FormattedMessage
                id="trips.changeModality.confirmation.text"
                defaultMessage="Are you sure you want to {action} the reserved modality {tripsCount}?"
                values={{
                  action: <FormattedMessage id="trips.changeModality.addReservedModality" defaultMessage="add" />,
                  tripsCount: (
                    <>
                      {tripsRef.current.length > 1 && (
                        <FormattedMessage
                          id="trips.changeModality.tripsCount"
                          defaultMessage="for {tripsCount} trips"
                          values={{ tripsCount: tripsRef.current.length }}
                        />
                      )}
                    </>
                  )
                }}
              />
            </CenteredText>
          </Column>
        </Row>
        <Row>
          <Column>
            <ImpactedSalesChannelsNotification salesChannels={impactedSalesChannels} />
          </Column>
        </Row>
      </Modal.Content>
      <Modal.Footer>
        {!isEmpty(errorsMessages) && (
          <>
            {errorsMessages.map((msg, index) => (
              <Modal.ErrorMessage key={index}>
                <ErrorMessage>{msg}</ErrorMessage>
              </Modal.ErrorMessage>
            ))}
          </>
        )}
        <Modal.Actions>
          <Button type="button" kind="cancel" onClick={closeModal}>
            <FormattedMessage id="modal.actions.close" defaultMessage="No, close" />
          </Button>
          <Button type="submit" kind="primary" loading={loading} onClick={handleChangeModality}>
            <FormattedMessage id="trips.changeModality.submit" defaultMessage="Apply change" />
          </Button>
        </Modal.Actions>
      </Modal.Footer>
    </Modal.Container>
  )
}
